<template>
  <v-container fluid>
    <v-row>
      <v-col cols="3">
        <v-autocomplete
          v-model="countryCode"
          :items="countryItems"
          :label="countryLabel"
          outlined
          :error-messages="getErrorMessages(v$.phone.country_prefix)"
          @input="v$.phone.country_prefix.$touch()"
          @blur="v$.phone.country_prefix.$touch()"
        />
      </v-col>
      <v-col cols="3">
        <v-text-field
          v-model="phone.phone_number"
          autocomplete="off"
          :label="phoneNumberLabel"
          outlined
          :hint="phoneNumberHint"
          persistent-hint
          :error-messages="getErrorMessages(v$.phone.phone_number)"
          @input="v$.phone.phone_number.$touch()"
          @blur="v$.phone.phone_number.$touch()"
        />
      </v-col>
      <v-col>
        <v-text-field
          label="Formatted Number"
          disabled
          outlined
          :value="formattedPhone"
        />
      </v-col>
    </v-row>
    <v-row>
      <!-- autocomplete phone_type:phoneTypesOptions -->
      <v-col cols="3">
        <v-autocomplete
          v-model="phone.phone_type"
          :items="phoneTypesOptions"
          :hint="phoneTypeHint"
          persistent-hint
          :label="phoneTypeLabel"
          outlined
          :error-messages="getErrorMessages(v$.phone.phone_type)"
          @input="v$.phone.phone_type.$touch()"
          @blur="v$.phone.phone_type.$touch()"
        />
      </v-col>
      <!-- text extension -->
      <v-col cols="3">
        <v-text-field
          v-model="phone.extension"
          autocomplete="off"
          label="Extension"
          outlined
          :error-messages="getErrorMessages(v$.phone.extension)"
          @input="v$.phone.extension.$touch()"
          @blur="v$.phone.extension.$touch()"
        />
      </v-col>
      <!-- text owner -->
      <v-col>
        <v-text-field
          v-model="phone.owner"
          autocomplete="off"
          label="Owner"
          outlined
          :error-messages="getErrorMessages(v$.phone.owner)"
          @input="v$.phone.owner.$touch()"
          @blur="v$.phone.owner.$touch()"
        />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { getCountryCodeForRegionCode, parsePhoneNumber } from 'awesome-phonenumber'
import { helpers, minLength, requiredIf } from '@vuelidate/validators'
import { mapActions, mapGetters } from 'vuex'
import { useVuelidate } from '@vuelidate/core'

import { SHARED_NAMESPACE } from '@/store/shared'

export default {
  name: 'PhoneFields',

  props: {
    required: {
      default: true,
      type: Boolean,
    },

    value: {
      default: _ => ({}),
      type: Object,
    },
  },

  setup () {
    return {
      v$: useVuelidate(),
    }
  },

  data () {
    return {
      countryIsoLocal: '',
    }
  },

  validations () {
    return {
      phone: {
        country_prefix: { required: requiredIf(this.isRequired) },
        extension: {},
        owner: {},

        phone_number: {
          isValid: helpers.withMessage(_ => this.phoneNumberHint, _ => {
            if (!this.isRequired && !this.phone.phone_number?.trim()) return true

            if (!this.phoneValidation.valid && this.phoneValidation.regionCode === 'US') {
              return this.phoneNumberIsException
            } else {
              return this.phoneValidation.valid
            }
          }),

          minlength: helpers.withMessage('Phone number must be at least 7 characters long', minLength(7)),

          noCountryCode: helpers.withMessage('Do not include the country code.', value => {
            if (!this.isRequired && !this.phone.phone_number?.trim()) return true

            const newValue = (/^[00|+].*$/.test(value) ? value : `+${value}`).replace(/^\+/, '00').replace(/[^\d]/g, '')
            const newFormattedValue = this.formattedPhone.replace(/^\+/, '00').replace(/[^\d]/g, '')
            return newValue !== newFormattedValue
          }),

          required: requiredIf(this.isRequired),
        },

        phone_type: { required: helpers.withMessage('Phone type is required', requiredIf(this.isRequired)) },
      },
    }
  },

  computed: {
    countryCode: {
      get () { return this.countryIsoLocal },

      set (iso) {
        this.countryIsoLocal = iso
        const selected = this.countryItems.find(c => c.value === iso)
        this.phone = {
          ...this.phone,
          country_id: selected?.id || null,
          country_iso_code: iso,
          country_prefix: getCountryCodeForRegionCode(iso),
        }
      },
    },

    countryItems () {
      return this.getCachedDropdown('country/countries').
        filter(c => c.iso_code && getCountryCodeForRegionCode(c.iso_code) > 0).
        map(c => ({ id: c.value, text: c.text, value: c.iso_code }))
    },

    countryLabel () { return this.isRequired ? 'Country *' : 'Country' },

    formattedPhone () {
      return this.phoneValidation?.number?.international
    },

    isRequired () {
      return this.required || this.phone.phone_number || this.phone.country_prefix || this.phone.phone_type
    },

    phone: {
      get () { return this.value },
      set (value) { this.$emit('input', value) },
    },

    phoneNumberHint () {
      return !this.locked && this.phone.phone_number
        ? `Phone Number ${this.phoneNumberIsException ? 'is-possible' : this.phoneValidation.possibility}`
        : ''
    },

    phoneNumberIsException () {
      // awesome-phonenumber lib latest version requires a node.js version we are not using yet, and numbers for the
      // Denver metro area are being detected as invalid by the lib version we are using, so we need these exceptions
      // until we upgrade to a new version
      return /^983(20[0-9]|210|212|213|222|333|444|777)[0-9]{4}/.test(this.phoneValidation.number?.significant)
    },

    phoneNumberLabel () { return this.isRequired ? 'Phone Number *' : 'Phone Number' },

    phoneTypeHint () {
      return !this.locked && this.phoneValidation.type ? `Phone appears to be ${this.phoneValidation.type}` : ''
    },

    phoneTypeLabel () { return this.isRequired ? 'Phone Type *' : 'Phone Type' },
    phoneTypesOptions () { return this.getCachedDropdown('phones/phone_type') },
    phoneValidation () { return parsePhoneNumber(this.phone.phone_number, { regionCode: this.countryIsoLocal }) },

    ...mapGetters(SHARED_NAMESPACE, [
      'getCachedDropdown',
    ]),
  },

  watch: {
    'v$.phone.$anyDirty' () { this.$emit('update:isDirty', this.v$.phone.$anyDirty) },
    'v$.phone.$invalid' () { this.$emit('update:isValid', this.v$.phone.$invalid) },

    value: {
      deep: true,

      handler: function () {
        this.countryIsoLocal = this.value.country_iso_code || ''
      },

      immediate: true,
    },
  },

  created () {
    this.getDropdownOptions({ path: 'phones/phone_type' })
    this.getDropdownOptions({ path: 'country/countries' })
  },

  methods: {
    getErrorMessages (validator) { return validator?.$errors.map(e => e.$message) },

    ...mapActions(SHARED_NAMESPACE, [
      'getDropdownOptions',
    ]),
  },
}
</script>
